import { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type RowSelectionState,
  type OnChangeFn,
} from '@tanstack/react-table';
import type { Density } from './useDataTablePrefs';

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  globalFilter?: string;
  density?: Density;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  getRowId?: (row: T) => string;
}

const densityPadding: Record<Density, string> = {
  compact: 'px-4 py-1.5',
  normal: 'px-4 py-3',
  comfortable: 'px-4 py-4',
};

const densityHeaderPadding: Record<Density, string> = {
  compact: 'px-4 py-2',
  normal: 'px-4 py-2.5',
  comfortable: 'px-4 py-3',
};

export function DataTable<T>({
  data,
  columns,
  sorting,
  onSortingChange,
  columnVisibility,
  onColumnVisibilityChange,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  globalFilter,
  density = 'normal',
  onRowClick,
  isLoading,
  emptyState,
  getRowId,
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection: rowSelection ?? {},
      globalFilter,
    },
    onSortingChange,
    onColumnVisibilityChange,
    enableRowSelection,
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId,
    globalFilterFn: 'includesString',
  });

  const cellPad = densityPadding[density];
  const headerPad = densityHeaderPadding[density];

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-line overflow-hidden">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex items-center gap-4 px-4 py-3.5 border-b border-line animate-pulse">
            <div className="w-8 h-8 rounded-lg bg-surface-subtle" />
            <div className="h-4 w-48 bg-surface-subtle rounded" />
            <div className="h-4 w-20 bg-surface-subtle rounded" />
            <div className="h-4 w-24 bg-surface-subtle rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-line overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} className="bg-surface-muted border-b border-line">
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className={`text-left text-[11px] font-semibold text-muted uppercase tracking-wider ${headerPad}`}
                    style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={`flex items-center gap-1.5 ${
                          header.column.getCanSort() ? 'cursor-pointer select-none hover:text-text' : ''
                        }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="text-subtle">
                            {{ asc: '\u2191', desc: '\u2193' }[header.column.getIsSorted() as string] ?? '\u2195'}
                          </span>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                className={`group transition-colors ${
                  onRowClick ? 'cursor-pointer hover:bg-surface-muted' : 'hover:bg-surface-muted/50'
                } ${row.getIsSelected() ? 'bg-coral-50/50' : ''}`}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className={cellPad}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {table.getRowModel().rows.length === 0 && (
        <div className="py-12">
          {emptyState ?? (
            <p className="text-center text-sm text-subtle">No data</p>
          )}
        </div>
      )}

      {/* Footer with row count */}
      {data.length > 0 && (
        <div className="px-4 py-2.5 bg-surface-muted border-t border-line text-xs text-muted flex items-center justify-between">
          <span>
            {table.getFilteredSelectedRowModel().rows.length > 0 && (
              <span className="font-medium text-coral-600">
                {table.getFilteredSelectedRowModel().rows.length} selected
              </span>
            )}
          </span>
          <span>
            Showing {table.getRowModel().rows.length} of {data.length}
          </span>
        </div>
      )}
    </div>
  );
}
